/**
 * OpenAI Text-to-Speech Types
 *
 * Type definitions for OpenAI TTS API integration
 */

import { z } from 'zod';

/**
 * Available OpenAI TTS voices
 */
export const TTSVoiceSchema = z.enum(['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer']);
export type TTSVoice = z.infer<typeof TTSVoiceSchema>;

/**
 * Available OpenAI TTS models
 */
export const TTSModelSchema = z.enum(['tts-1', 'tts-1-hd', 'gpt-4o-mini-tts']);
export type TTSModel = z.infer<typeof TTSModelSchema>;

/**
 * Available audio formats
 */
export const TTSFormatSchema = z.enum(['mp3', 'opus', 'aac', 'flac', 'wav', 'pcm']);
export type TTSFormat = z.infer<typeof TTSFormatSchema>;

/**
 * TTS synthesis options (subset of request, used for service calls)
 */
export const TTSSynthesisOptionsSchema = z.object({
  voice: TTSVoiceSchema.optional(),
  speed: z.number().min(0.25).max(4.0).optional(),
  instructions: z.string().max(256).optional(),
  model: TTSModelSchema.optional(),
  format: TTSFormatSchema.optional(),
});
export type TTSSynthesisOptions = z.infer<typeof TTSSynthesisOptionsSchema>;

/**
 * Maximum characters per TTS chunk sent to the OpenAI API.
 * Texts longer than this are split at sentence boundaries server-side.
 * Lower this value (e.g. 200) to exercise chunking during development.
 */
export const TTS_CHUNK_SIZE = 1024; //4096;

/**
 * TTS synthesis request
 */
export const TTSSynthesisRequestSchema = z.object({
  text: z
    .string()
    .min(1, 'Text cannot be empty')
    .max(50000, 'Text too long (max 50000 characters)'),
  voice: TTSVoiceSchema.optional(),
  speed: z.number().min(0.25).max(4.0).optional(),
  instructions: z.string().max(256).optional(),
  model: TTSModelSchema.optional(),
  format: TTSFormatSchema.optional(),
});

export type TTSSynthesisRequest = z.infer<typeof TTSSynthesisRequestSchema>;

/**
 * TTS synthesis response
 */
export const TTSSynthesisResponseSchema = z.object({
  audio: z.string().describe('Base64 encoded audio data'),
  format: TTSFormatSchema.describe('Audio format'),
  sizeBytes: z.number().describe('Audio size in bytes'),
});

export type TTSSynthesisResponse = z.infer<typeof TTSSynthesisResponseSchema>;
